/**
 * useUpload.js
 * Hook for CSV upload + detection flow used by Upload.jsx
 */

import { useState } from 'react';

const WORKER_API = process.env.REACT_APP_WORKER_API || '/worker-api/api';

const STEPS = [
  'Parsing CSV',
  'Preprocessing features',
  'Running AE anomaly scoring',
  'Running LightGBM inference',
  'Aggregating alerts',
  'Building incidents',
];

export function useUpload() {
  const [status,   setStatus]   = useState('idle');
  const [progress, setProgress] = useState(null);
  const [result,   setResult]   = useState(null);
  const [preview,  setPreview]  = useState(null);
  const [error,    setError]    = useState('');

  // ── upload ──────────────────────────────────────────────────────────────
  async function upload(file) {
    if (!file) return;
    setStatus('uploading');
    setProgress(null);
    setResult(null);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${WORKER_API}/upload`, {
        method: 'POST',
        body:   formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.detail || err.error || `Upload failed: ${res.status}`);
      }
      const data = await res.json();
      setPreview({
        filename: data.filename || file.name,
        rows:     data.rows     || 0,
        columns:  data.columns  || [],
        preview:  data.preview  || [],
      });
      setStatus('ready');
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  }

  // ── detect ───────────────────────────────────────────────────────────────
  // POST /detect takes no body — backend uses soc_state["raw_df"] set by /upload
  async function detect(onComplete) {
    setStatus('detecting');
    setResult(null);
    setError('');

    let step = 0;
    setProgress({ step: 0, total: STEPS.length, label: STEPS[0] });

    const stepTimer = setInterval(() => {
      step = Math.min(step + 1, STEPS.length - 1);
      setProgress({ step, total: STEPS.length, label: STEPS[step] });
    }, 800);

    try {
      const res = await fetch(`${WORKER_API}/detect`, { method: 'POST' });
      clearInterval(stepTimer);

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.detail || err.error || `Detection failed: ${res.status}`);
      }

      const data = await res.json();

      // Normalise field names — backend returns various shapes
      const normalised = {
        total_samples:        data.total          || data.total_samples        || 0,
        total_attacks:        data.attacks        || data.attacks_detected     || 0,
        attacks_detected:     data.attacks        || data.attacks_detected     || 0,
        normal_traffic:       data.normal         || data.normal_traffic       || 0,
        total_normal:         data.normal         || data.normal_traffic       || 0,
        incidents_clustered:  data.incidents?.length ?? data.incidents_clustered ?? 0,
        severity_distribution: data.severity_counts || data.severity_distribution || {},
        alerts:               data.alerts         || [],
        incidents:            data.incidents      || [],
        model_info:           data.model_info     || {},
        ...data,
      };

      setProgress({ step: STEPS.length, total: STEPS.length, label: 'Complete' });
      setResult(normalised);
      setStatus('complete');

      if (onComplete) onComplete(normalised);
    } catch (err) {
      clearInterval(stepTimer);
      setError(err.message);
      setStatus('error');
      setProgress(null);
    }
  }

  return { status, progress, result, preview, error, upload, detect };
}
